import { describe, it, expect } from '@jest/globals';
import { _applyBuiltInSettings, createBuiltInSettingsTransform } from './createBuiltInSettingsTransform.js';
import { getBuiltInSettings } from './getBuiltInSettings.js';
import { MockPackageDefinitions } from '@ms-cloudpack/package-utilities';
import type { PackageJson } from '@ms-cloudpack/common-types';
import { cloneJson } from '@ms-cloudpack/json-utilities';

describe('_applyBuiltInSettings', () => {
  /**
   * Call `_applyBuiltInSettings` on a copy of `definition`.
   * (The transform mutates the definition, so copying it allows proper before/after comparison.)
   */
  function applyBuiltInSettingsWrapper(definition: PackageJson): PackageJson | undefined {
    return _applyBuiltInSettings(cloneJson(definition));
  }

  it('applies matching settings for a package', () => {
    const newDefinition = applyBuiltInSettingsWrapper({
      name: 'scheduler',
      version: '0.9.0',
      type: 'commonjs',
    });

    expect(newDefinition).toEqual({
      name: 'scheduler',
      version: '0.9.0',
      type: 'commonjs', // extra property preserved
      exports: {
        '.': './index.js',
        './tracing': './tracing.js',
      },
    });
  });

  it('applies matching settings for scoped package', () => {
    const newDefinition = applyBuiltInSettingsWrapper({
      name: '@microsoft/recognizers-text-date-time',
      version: '1.1.0',
    });

    expect(newDefinition).toEqual({
      name: '@microsoft/recognizers-text-date-time',
      version: '1.1.0',
      exports: './dist/recognizers-text-date-time.es5.js',
    });
  });

  it('returns undefined when no settings for name', () => {
    expect(applyBuiltInSettingsWrapper({ name: 'nope', version: '1.0.0' })).toBeUndefined();
  });

  it("returns undefined when version doesn't match", () => {
    expect(applyBuiltInSettingsWrapper({ name: 'scheduler', version: '100.0.0' })).toBeUndefined();
  });

  it('returns undefined when only PackageSettings exist for that package', () => {
    // ensure test assumption is still valid
    const definition = { name: 'asap', version: '2.0.0' };
    expect(getBuiltInSettings(definition)).toEqual({ packageSettings: expect.anything() });

    expect(applyBuiltInSettingsWrapper(definition)).toBeUndefined();
  });
});

// All-up sanity check test
describe('createBuiltInSettingsTransform', () => {
  it('applies matching settings for a package', async () => {
    const testPath = 'fake/path';
    const definitions = new MockPackageDefinitions({
      fixture: { [testPath]: { name: 'scheduler', version: '0.9.0', type: 'commonjs' } },
      transformFactories: [() => createBuiltInSettingsTransform()],
    });

    expect(await definitions.get(testPath)).toEqual({
      name: 'scheduler',
      version: '0.9.0',
      type: 'commonjs',
      exports: {
        '.': './index.js',
        './tracing': './tracing.js',
      },
    });
  });
});
